import fallbackImage from "@assets/images/fallback-game.svg";
import {IconHeart, IconHeartFilled, IconHistory, IconMessageCircle, IconTrophy, IconX} from "@tabler/icons-react";
import {memo, useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router-dom";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import {useGetBigWinners} from "@/hooks/gamesList/useGetBigWinners";
import {useMiniGames} from "@/store/useMiniGames";
import {formatAmountSuffix} from "@/util/balance.util";
import {formatCurrency} from "@/util/formatCurrency";
import carouselStyles from "../StoriesCarousel/StoriesCarousel.module.css";
import StoryItem from "../StoryItem/StoryItem";
import AYGStoryViewer from "../StoryViewers/AYGStoryViewer";
import {getProviderLogo} from "../stories.constants";
import styles from "./ProfileHighlights.module.css";

/**
 * Profile Highlights — Personal story highlights for the user profile.
 * Shows: Big Wins, Favorites, Recent Games, and user's top providers.
 *
 * @param {Object} props
 * @param {Array} props.favoriteGames - User's favorite games
 * @param {Array} props.recentGames - User's recent games
 * @param {Array<{name: string, count: number}>} props.favoriteProviders - User's top providers
 */
const ProfileHighlights = ({favoriteGames = [], recentGames = [], favoriteProviders = [], userAlias = ""}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {winners} = useGetBigWinners();
  const storeSetFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const [activeHighlight, setActiveHighlight] = useState(null);

  const userWins = useMemo(
    () => (userAlias ? winners.filter(w => w.user_alias === userAlias) : []),
    [winners, userAlias]
  );

  /** Convert a winner entry to a bigwin story slide */
  const toBigWinStory = useCallback(
    (winner, index) => ({
      id: `profile-bigwin-${index}`,
      name: `@${winner.user_alias}`,
      src: winner.icon || fallbackImage,
      mediaSrc: winner.video_url || null,
      type: winner.video_url ? "video" : "bigwin",
      winnerAlias: winner.user_alias,
      winnerAmount: winner.amount_float,
      winnerCurrency: winner.currency,
      winnerGame: winner.game_name,
    }),
    []
  );

  const bigWinStories = useMemo(() => userWins.map((w, i) => toBigWinStory(w, i)), [userWins, toBigWinStory]);

  const handleOpenGame = useCallback(
    (gameId, provider) => {
      setActiveHighlight(null);
      navigate(`/games/${provider}/${gameId}`);
    },
    [navigate]
  );

  const handleImgError = useCallback(e => {
    e.target.onerror = null;
    e.target.src = fallbackImage;
  }, []);

  const isGameFavorited = useCallback(
    (gameId, provider) => storeFavoriteGames.some(f => f.game_id === gameId && f.game_provider === provider),
    [storeFavoriteGames]
  );

  const handleToggleFavorite = useCallback(
    (e, game) => {
      e.stopPropagation();
      e.preventDefault();
      const id = game.game_id || game.id;
      const provider = game.game_provider || game.provider;
      storeSetFavoriteGames({id, provider, game_provider: provider}, addFavoriteGame, deleteFavoriteGame);
    },
    [storeSetFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  /** Build highlight circles */
  const highlights = useMemo(() => {
    const items = [];

    items.push({
      id: "highlight-bigwins",
      label: t("profileHighlightBigWins"),
      icon: "",
      type: "highlight",
      ringVariant: "bigwin",
      highlightType: "bigwins",
    });

    if (favoriteGames.length > 0) {
      items.push({
        id: "highlight-favorites",
        label: t("profileHighlightFavorites"),
        icon: "",
        type: "highlight",
        ringVariant: "favorites",
        highlightType: "favorites",
      });
    }

    if (recentGames.length > 0) {
      items.push({
        id: "highlight-recent",
        label: t("profileHighlightRecent"),
        icon: "",
        type: "highlight",
        ringVariant: "recent",
        highlightType: "recent",
      });
    }

    for (const prov of favoriteProviders) {
      const logo = getProviderLogo(prov.name);
      if (logo) {
        items.push({
          id: `highlight-prov-${prov.name}`,
          label: prov.name,
          icon: logo,
          type: "provider",
          ringVariant: "provider",
          providerKey: prov.name,
        });
      }
    }

    return items;
  }, [t, favoriteGames.length, recentGames.length, favoriteProviders]);

  const handleOpen = useCallback(story => {
    setActiveHighlight(story.highlightType || story.providerKey || null);
  }, []);

  const handleClose = useCallback(() => {
    setActiveHighlight(null);
  }, []);

  const renderHighlightIcon = type => {
    if (type === "bigwins") return <IconTrophy size={26} />;
    if (type === "favorites") return <IconHeart size={26} />;
    if (type === "recent") return <IconHistory size={26} />;
    return null;
  };

  if (highlights.length === 0) return null;

  return (
    <>
      <section
        className={carouselStyles.stories}
        aria-label={t("profileHighlightBigWins")}
      >
        <div className={carouselStyles.stories__row}>
          {highlights.map(h => (
            <div
              key={h.id}
              className={styles.highlightItem}
            >
              {h.type === "highlight" ? (
                <HighlightCircle
                  highlight={h}
                  icon={renderHighlightIcon(h.highlightType)}
                  onOpen={handleOpen}
                />
              ) : (
                <StoryItem
                  story={h}
                  onOpen={handleOpen}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Big Wins Viewer — IG-style fullscreen stories */}
      {activeHighlight === "bigwins" && bigWinStories.length > 0 && (
        <AYGStoryViewer
          stories={bigWinStories}
          initialIndex={0}
          onClose={handleClose}
          displayName={t("profileHighlightBigWins")}
        />
      )}
      {activeHighlight === "bigwins" && bigWinStories.length === 0 && (
        <BigWinsViewer
          winners={[]}
          onClose={handleClose}
          onGameClick={(provider, gameId) => handleOpenGame(gameId, provider)}
          onImgError={handleImgError}
          t={t}
        />
      )}

      {/* Favorites Viewer */}
      {activeHighlight === "favorites" && (
        <GameGridViewer
          title={t("profileHighlightFavorites")}
          icon={<IconHeart size={18} />}
          iconBg={styles.iconBgFavorites}
          games={favoriteGames}
          onClose={handleClose}
          onGameClick={game => handleOpenGame(game.game_id || game.id, game.game_provider || game.provider)}
          onImgError={handleImgError}
          sectionTitle={t("profileHighlightPopularGames")}
          onToggleFavorite={handleToggleFavorite}
          isGameFavorited={isGameFavorited}
        />
      )}

      {/* Recent Games Viewer */}
      {activeHighlight === "recent" && (
        <GameGridViewer
          title={t("profileHighlightRecent")}
          icon={<IconHistory size={18} />}
          iconBg={styles.iconBgRecent}
          games={recentGames}
          onClose={handleClose}
          onGameClick={game => handleOpenGame(game.game_id || game.id, game.game_provider || game.provider)}
          onImgError={handleImgError}
          sectionTitle={t("profileHighlightLastPlayed")}
          onToggleFavorite={handleToggleFavorite}
          isGameFavorited={isGameFavorited}
        />
      )}
    </>
  );
};

/** Highlight circle with icon (non-image based) */
const HighlightCircle = memo(({highlight, icon, onOpen}) => {
  const handleClick = useCallback(() => {
    onOpen(highlight);
  }, [onOpen, highlight]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(highlight);
      }
    },
    [onOpen, highlight]
  );

  const ringClass = `${styles.highlightRing} ${styles[`highlightRing--${highlight.ringVariant}`] || ""}`;

  return (
    <div
      className={styles.highlightCircle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={highlight.label}
    >
      <div className={ringClass}>
        <div className={styles.highlightAvatar}>
          <span className={styles.highlightIcon}>{icon}</span>
        </div>
      </div>
      <span className={styles.highlightLabel}>{highlight.label}</span>
    </div>
  );
});

/** Big Wins viewer — fullscreen winners list */
const BigWinsViewer = memo(({winners, onClose, onGameClick, onImgError, t}) => {
  return (
    <div
      className={styles.viewerOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={t("profileHighlightBigWins")}
    >
      <div className={styles.viewerContainer}>
        <div className={styles.viewerHeader}>
          <div className={styles.headerIconBigwin}>
            <IconTrophy size={18} />
          </div>
          <span className={styles.headerName}>{t("profileHighlightBigWins")}</span>
          <button
            type="button"
            className={styles.viewerClose}
            onClick={onClose}
            aria-label="Close"
          >
            <IconX size={22} />
          </button>
        </div>
        <div className={styles.viewerContent}>
          <div className={styles.viewerSection}>
            <span className={styles.viewerSectionTitle}>
              <IconTrophy size={14} /> {t("profileHighlightLatestWinners")}
            </span>
            <div className={styles.winnersList}>
              {winners.map((winner, idx) => (
                <div
                  key={`bw-${winner.user_alias}-${winner.game_id}-${idx}`}
                  className={styles.winnerCard}
                  onClick={() => winner.game_id && winner.provider && onGameClick(winner.provider, winner.game_id)}
                  onKeyDown={e =>
                    e.key === "Enter" &&
                    winner.game_id &&
                    winner.provider &&
                    onGameClick(winner.provider, winner.game_id)
                  }
                  role="button"
                  tabIndex={0}
                >
                  <img
                    className={styles.winnerCardImg}
                    src={winner.icon || fallbackImage}
                    alt={winner.game_name}
                    loading="lazy"
                    onError={onImgError}
                  />
                  <div className={styles.winnerCardInfo}>
                    <Link
                      to={`/u/${winner.user_alias}`}
                      className={styles.winnerCardUserLink}
                      onClick={e => e.stopPropagation()}
                    >
                      @{winner.user_alias}
                    </Link>
                    <span className={styles.winnerCardGame}>{winner.game_name}</span>
                  </div>
                  <span className={styles.winnerCardAmount}>
                    {formatAmountSuffix(winner.amount_float)} {formatCurrency(winner.currency)}
                  </span>
                </div>
              ))}
              {winners.length === 0 && <div className={styles.viewerEmpty}>{t("profileHighlightNoWins")}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

/** Game grid viewer — fullscreen game thumbnail grid */
const GameGridViewer = memo(
  ({title, icon, iconBg, games, onClose, onGameClick, onImgError, sectionTitle, onToggleFavorite, isGameFavorited}) => {
    return (
      <div
        className={styles.viewerOverlay}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.viewerContainer}>
          <div className={styles.viewerHeader}>
            <div className={`${styles.headerIcon} ${iconBg}`}>{icon}</div>
            <span className={styles.headerName}>{title}</span>
            <button
              type="button"
              className={styles.viewerClose}
              onClick={onClose}
              aria-label="Close"
            >
              <IconX size={22} />
            </button>
          </div>
          <div className={styles.viewerContent}>
            <div className={styles.viewerSection}>
              <span className={styles.viewerSectionTitle}>{sectionTitle}</span>
              <div className={styles.gamesGrid}>
                {games.slice(0, 9).map(game => {
                  const gId = game.game_id || game.id;
                  const gProv = game.game_provider || game.provider;
                  const isFav = isGameFavorited?.(gId, gProv);
                  return (
                    <div
                      key={gId}
                      className={styles.gameThumb}
                      onClick={() => onGameClick(game)}
                      onKeyDown={e => e.key === "Enter" && onGameClick(game)}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        className={styles.gameThumbImg}
                        src={game.icon || fallbackImage}
                        alt={game.name || game.game_name}
                        loading="lazy"
                        onError={onImgError}
                      />
                      {onToggleFavorite && (
                        <button
                          type="button"
                          className={styles.gameThumbLikeBtn}
                          onClick={e => onToggleFavorite(e, game)}
                          aria-label={isFav ? "Unlike" : "Like"}
                        >
                          {isFav ? <IconHeartFilled size={14} /> : <IconHeart size={14} />}
                        </button>
                      )}
                      <div className={styles.gameThumbOverlay}>
                        <span className={styles.gameThumbStat}>
                          <IconHeartFilled size={14} />
                          {game.like_count ?? 0}
                        </span>
                        <span className={styles.gameThumbStat}>
                          <IconMessageCircle size={14} />
                          {game.comments_count ?? 0}
                        </span>
                      </div>
                      <span className={styles.gameThumbName}>{game.name || game.game_name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default memo(ProfileHighlights);
